import {useCallback, useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import {getCurrentUserData} from '../redux/user/thunks/getCurrentUserData';
import {updateUserState} from '../redux/user/userSlice';
import {
  activateCurrentBrowserWindow,
  AI_CALL_INCOMING_DISMISSED_KEY,
  AI_CALL_INCOMING_REQUESTED_KEY,
  confirmAiCall,
  fetchPendingAiCall,
  getIncomingCallFromSocketData,
  notifyAiCallIncomingDismissed,
  notifyAiCallIncomingRequested,
  parseAiCallIncomingDismissed,
  parseAiCallIncomingRequested,
  AI_CALL_INSPECTION_PROMPT_DISMISSED_KEY,
  AI_CALL_INSPECTION_PROMPT_REQUESTED_KEY,
  getInspectionJobIdFromSocketData,
  notifyAiCallInspectionPromptDismissed,
  notifyAiCallInspectionPromptRequested,
  openAiCallInspectionTab,
  parseAiCallInspectionPromptDismissed,
  parseAiCallInspectionPromptRequested,
} from '../utils/aiCallSession';
import {
  acknowledgeAiCallPrompt,
  AI_CALL_ACCEPTED_FROM_NOTIFICATION,
  closeIncomingCallNotification,
  notifyAiCallTabClosing,
  onAiCallServiceWorkerMessage,
  registerAiCallServiceWorker,
  showIncomingCallNotification,
  showMissedCallNotification,
  takeCallAcceptedFromNotification,
} from '../utils/aiCallNotifications';
import {
  connectUserSocket,
  disconnectUserSocket,
  getBrowserSessionId,
  getUserSocket,
  SOCKET_EVENTS,
} from '../sockets/userSocket';

/**
 * Connects one Socket.IO client per browser tab (auth includes `browserSessionId`).
 * Multiple tabs/windows for the same user each get their own socket; the server must
 * allow multiple connections per user (e.g. join `user:${userId}` for every tab).
 *
 * @param {object} [opts]
 * @param {boolean} [opts.enabled=true] — set false to disconnect (e.g. logged out or !acceptAiCall)
 * @param {string} [opts.userId] — sent as auth.userId (should match JWT sub; server must verify)
 * @param {boolean} [opts.acceptAiCall] — sent as auth.acceptAiCall so the server can align subscription
 * @param {(data: unknown) => void} [opts.onDataReady] — handler for {@link SOCKET_EVENTS.DATA_READY}
 */
export function useUserSocket(opts = {}) {
  const dispatch = useDispatch();
  const {enabled = true, userId, acceptAiCall, onDataReady} = opts;
  const onDataReadyRef = useRef(onDataReady);
  onDataReadyRef.current = onDataReady;
  const browserSessionIdRef = useRef(getBrowserSessionId());
  const [pendingInspectionJobId, setPendingInspectionJobId] = useState(null);
  const pendingInspectionJobIdRef = useRef(null);
  // Incoming AI call (accept/decline) popup state.
  const [incomingCall, setIncomingCall] = useState(null);
  const incomingCallRef = useRef(null);
  const [incomingCallStatus, setIncomingCallStatus] = useState(null); // null | 'passed'
  /** Set when an accepted call had no inspection attached, so the agent has to find it. */
  const [inspectionSearchCall, setInspectionSearchCall] = useState(null);
  const [incomingCallSubmitting, setIncomingCallSubmitting] = useState(false);
  /**
   * Desktop notifications are for an agent who is on duty. The prompt carries the server's
   * `agentAvailable`, which wins: this tab's copy can be stale, e.g. after a call was accepted in
   * another tab or from the notification. The tab's copy is only the fallback, read through a ref
   * rather than an effect dependency, which would reconnect the socket on every toggle.
   */
  const aiAvailable = useSelector((state) => Boolean(state.user?.aiAvailable));
  const aiAvailableRef = useRef(aiAvailable);
  aiAvailableRef.current = aiAvailable;
  const showIncomingCallNotificationIfAvailable = useCallback((data) => {
    const available = typeof data?.agentAvailable === 'boolean'
      ? data.agentAvailable
      : aiAvailableRef.current;
    if (available) {
      showIncomingCallNotification(data);
    }
  }, []);

  useEffect(() => {
    pendingInspectionJobIdRef.current = pendingInspectionJobId;
  }, [pendingInspectionJobId]);

  useEffect(() => {
    incomingCallRef.current = incomingCall;
  }, [incomingCall]);

  const dismissAiCallInspectionPromptLocally = useCallback(() => {
    setPendingInspectionJobId(null);
  }, []);

  const notifyOtherTabsAiCallInspectionPromptDismissed = useCallback((inspectionJobId, action) => {
    notifyAiCallInspectionPromptDismissed({
      userId,
      inspectionJobId,
      action,
    });
  }, [userId]);

  const refreshUserAvailability = useCallback(() => {
    dispatch(getCurrentUserData({silent: true}));
  }, [dispatch]);

  const closeAiCallInspectionPrompt = useCallback(() => {
    const inspectionJobId = pendingInspectionJobIdRef.current;
    dismissAiCallInspectionPromptLocally();
    refreshUserAvailability();
    if (inspectionJobId) {
      notifyOtherTabsAiCallInspectionPromptDismissed(inspectionJobId, 'close');
    }
  }, [
    dismissAiCallInspectionPromptLocally,
    notifyOtherTabsAiCallInspectionPromptDismissed,
    refreshUserAvailability,
  ]);

  const openPendingAiCallInspectionTab = useCallback(() => {
    const inspectionJobId = pendingInspectionJobIdRef.current;
    if (!inspectionJobId) {
      return;
    }
    openAiCallInspectionTab(inspectionJobId);
    dismissAiCallInspectionPromptLocally();
    refreshUserAvailability();
    notifyOtherTabsAiCallInspectionPromptDismissed(inspectionJobId, 'open');
  }, [
    dismissAiCallInspectionPromptLocally,
    notifyOtherTabsAiCallInspectionPromptDismissed,
    refreshUserAvailability,
  ]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const matchesUser = (payloadUserId) => {
      if (!userId || !payloadUserId) {
        return true;
      }
      return payloadUserId === String(userId);
    };

    const onStorage = (event) => {
      if (event.newValue == null) {
        return;
      }

      if (event.key === AI_CALL_INCOMING_REQUESTED_KEY) {
        const payload = parseAiCallIncomingRequested(event.newValue);
        if (!payload || !matchesUser(payload.userId)) {
          return;
        }
        dispatch(getCurrentUserData({silent: true}));
        setIncomingCallStatus(null);
        setIncomingCall(payload.call);
        return;
      }

      if (event.key === AI_CALL_INCOMING_DISMISSED_KEY) {
        const payload = parseAiCallIncomingDismissed(event.newValue);
        if (!payload || !matchesUser(payload.userId)) {
          return;
        }
        const current = incomingCallRef.current;
        if (!current || current.callId === payload.callId) {
          setIncomingCall(null);
        }
        return;
      }

      if (event.key === AI_CALL_INSPECTION_PROMPT_REQUESTED_KEY) {
        const payload = parseAiCallInspectionPromptRequested(event.newValue);
        if (!payload || !matchesUser(payload.userId)) {
          return;
        }
        dispatch(getCurrentUserData({silent: true}));
        setPendingInspectionJobId(payload.inspectionJobId);
        return;
      }

      if (event.key === AI_CALL_INSPECTION_PROMPT_DISMISSED_KEY) {
        const payload = parseAiCallInspectionPromptDismissed(event.newValue);
        if (!payload || !matchesUser(payload.userId)) {
          return;
        }

        if (pendingInspectionJobIdRef.current === payload.inspectionJobId) {
          setPendingInspectionJobId(null);
          dispatch(getCurrentUserData({silent: true}));
        }
      }
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [dispatch, enabled, userId]);

  useEffect(() => {
    if (!pendingInspectionJobId) {
      return undefined;
    }

    activateCurrentBrowserWindow();
  }, [pendingInspectionJobId]);

  /**
   * Desktop notifications. Registers the service worker that raises them, and tells it when this
   * tab goes away, so a prompt does not stay on screen with no portal tab left to back it.
   *
   * A call accepted from the notification with no job attached still needs the property found.
   * The service worker leaves it for a tab to collect: this one, if it was open, or the one it
   * opened.
   */
  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    let cancelled = false;

    const collectAcceptedCall = async () => {
      const data = await takeCallAcceptedFromNotification();
      const call = data ? getIncomingCallFromSocketData(data) : null;
      if (!cancelled && call) {
        setInspectionSearchCall(call);
      }
    };

    registerAiCallServiceWorker().then(() => {
      if (!cancelled) {
        collectAcceptedCall();
      }
    });

    window.addEventListener('pagehide', notifyAiCallTabClosing);

    const unsubscribe = onAiCallServiceWorkerMessage((message) => {
      if (message.type === AI_CALL_ACCEPTED_FROM_NOTIFICATION) {
        collectAcceptedCall();
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
      window.removeEventListener('pagehide', notifyAiCallTabClosing);
    };
  }, [enabled, userId]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const browserSessionId = browserSessionIdRef.current;
    let cancelled = false;

    const dataReadyListener = (data) => {
      if (data && typeof data === 'object' && data.type === 'ai-call-agent-unavailable') {
        dispatch(getCurrentUserData({silent: true}));
        const inspectionJobId = getInspectionJobIdFromSocketData(data);
        if (inspectionJobId) {
          setPendingInspectionJobId(inspectionJobId);
          notifyAiCallInspectionPromptRequested({
            userId,
            inspectionJobId,
          });
        }
      }

      if (data && typeof data === 'object' && data.type === 'ai-call-incoming') {
        const call = getIncomingCallFromSocketData(data);
        if (call) {
          dispatch(getCurrentUserData({silent: true}));
          setIncomingCallStatus(null);
          setIncomingCall(call);
          notifyAiCallIncomingRequested({userId, call});
          // The in-page prompt is invisible in a background tab; the desktop notification is not.
          showIncomingCallNotificationIfAvailable(data);
          acknowledgeAiCallPrompt(call.answerToken, 'socket');
        }
      }

      /**
       * The agent took a call, or one of theirs ended. Drives the navbar's End Call button, which
       * is why it is a socket event and not a poll: the button has to appear the moment they
       * accept and go the moment it is over, including when a supervisor closed it for them.
       */
      if (data && typeof data === 'object' && data.type === 'ai-call-started') {
        dispatch(updateUserState({aiOpenCall: data.call ?? null}));
      }

      if (data && typeof data === 'object' && data.type === 'ai-call-ended') {
        dispatch(updateUserState({aiOpenCall: null}));
      }

      if (data && typeof data === 'object' && data.type === 'ai-call-confirmation-closed') {
        const closedCallId = getIncomingCallFromSocketData(data)?.callId || '';
        const current = incomingCallRef.current;
        if (data.reason === 'expired') {
          showMissedCallNotification(data.answerToken);
        } else {
          closeIncomingCallNotification(data.answerToken);
        }
        if (current && (!closedCallId || current.callId === closedCallId)) {
          setIncomingCall(null);
          notifyAiCallIncomingDismissed({userId, callId: current.callId});
        }
        /**
         * Accepting a call takes the agent off the queue on the server, wherever they accepted it
         * (another tab, the desktop notification), so every tab reloads the user to keep the
         * navbar's Available checkbox honest.
         */
        if (data.reason === 'answered') {
          dispatch(getCurrentUserData({silent: true}));
        }
        /**
         * Nobody answered in time, so the server has marked this agent unavailable. Reload the
         * user so the navbar checkbox says so, and tell them why it changed under them. Checked
         * without `current`: the local countdown usually closes the prompt a moment earlier.
         */
        if (data.reason === 'expired') {
          dispatch(getCurrentUserData({silent: true}));
          toast(
            'You missed an AI call transfer, so you have been set to unavailable. '
              + 'Tick Available in the navbar when you are ready for calls again.',
            {type: 'warning', toastId: `ai-call-missed-${closedCallId || 'call'}`},
          );
        }
      }

      onDataReadyRef.current?.(data);
    };

    /**
     * Picks up a prompt the socket never delivered. The server emits `ai-call-incoming` once and
     * does not retry, so a call handed over while this tab was connecting - or reconnecting after
     * a backend deploy - would otherwise never appear, and the agent would miss it without ever
     * seeing a popup.
     */
    const syncPendingCall = async () => {
      const data = await fetchPendingAiCall();
      if (cancelled || !data || incomingCallRef.current) {
        return;
      }
      const call = getIncomingCallFromSocketData(data);
      if (!call) {
        return;
      }
      setIncomingCallStatus(null);
      setIncomingCall(call);
      notifyAiCallIncomingRequested({userId, call});
      showIncomingCallNotificationIfAvailable(data);
      acknowledgeAiCallPrompt(call.answerToken, 'socket');
    };

    const extraAuth = {
      ...(userId != null && userId !== '' ? {userId} : {}),
      ...(typeof acceptAiCall === 'boolean' ? {acceptAiCall} : {}),
    };

    const run = async () => {
      const socket = await connectUserSocket({
        browserSessionId,
        ...(Object.keys(extraAuth).length ? {extraAuth} : {}),
      });
      if (cancelled || !socket) {
        return;
      }

      socket.on(SOCKET_EVENTS.DATA_READY, dataReadyListener);
      socket.on('connect', () => {
        if (process.env.NODE_ENV === 'development') {
          console.log('user socket connected', socket.id, browserSessionId);
        }
        syncPendingCall();
      });
      // `connect` does not fire for a socket that was already up when this effect ran.
      if (socket.connected) {
        syncPendingCall();
      }
      /**
       * `Unauthorized` here means the handshake was rejected, not that the network is down, and
       * socket.io retries forever. Named explicitly because the only symptom otherwise is that
       * incoming-call prompts quietly stop arriving.
       */
      socket.on('connect_error', (err) =>
        console.error('user socket connect failed:', err.message)
      );
    };

    run();

    return () => {
      cancelled = true;
      const socket = getUserSocket(browserSessionId);
      if (socket) {
        socket.off(SOCKET_EVENTS.DATA_READY, dataReadyListener);
        socket.off('connect');
        socket.off('connect_error');
      }
      disconnectUserSocket(browserSessionId);
    };
  }, [dispatch, enabled, userId, acceptAiCall, showIncomingCallNotificationIfAvailable]);

  const clearIncomingCallLocally = useCallback(() => {
    setIncomingCall(null);
  }, []);

  // Accept (confirmed=true) or decline (confirmed=false) the current incoming call.
  const settleIncomingCallPost = useCallback((confirmed) => {
    const call = incomingCallRef.current;
    if (!call || incomingCallSubmitting) {
      return;
    }
    const {callId, inspectionJobId} = call;
    setIncomingCallSubmitting(true);
    // Tell sibling tabs to close now so only this tab drives the decision.
    notifyAiCallIncomingDismissed({userId, callId});
    closeIncomingCallNotification(call.answerToken);
    confirmAiCall({callId, confirmed}).then((result) => {
      setIncomingCallSubmitting(false);
      if (result.passedToAnotherAgent) {
        clearIncomingCallLocally();
        setIncomingCallStatus('passed');
        refreshUserAvailability();
        return;
      }
      clearIncomingCallLocally();
      if (confirmed && result.ok) {
        if (inspectionJobId) {
          openAiCallInspectionTab(inspectionJobId);
        } else if (call.needsInspectionSearch) {
          // The call is connected either way; this only identifies the property. Closing the
          // dialog without linking leaves the call recorded as unmatched.
          setInspectionSearchCall(call);
        }
      }
      refreshUserAvailability();
    });
  }, [incomingCallSubmitting, userId, clearIncomingCallLocally, refreshUserAvailability]);

  const acceptIncomingCall = useCallback(() => {
    settleIncomingCallPost(true);
  }, [settleIncomingCallPost]);

  const declineIncomingCall = useCallback(() => {
    settleIncomingCallPost(false);
  }, [settleIncomingCallPost]);

  // Countdown hit 0: the server already passed it, so just close (no POST).
  const expireIncomingCall = useCallback(() => {
    const call = incomingCallRef.current;
    if (!call) {
      return;
    }
    clearIncomingCallLocally();
    notifyAiCallIncomingDismissed({userId, callId: call.callId});
    closeIncomingCallNotification(call.answerToken);
  }, [clearIncomingCallLocally, userId]);

  const closeIncomingCallStatus = useCallback(() => {
    setIncomingCallStatus(null);
  }, []);

  useEffect(() => {
    if (!incomingCall) {
      return undefined;
    }
    activateCurrentBrowserWindow();
    return undefined;
  }, [incomingCall]);

  return {
    aiCallInspectionPrompt: {
      open: Boolean(pendingInspectionJobId),
      onClose: closeAiCallInspectionPrompt,
      onOpen: openPendingAiCallInspectionTab,
    },
    aiCallInspectionSearch: {
      open: Boolean(inspectionSearchCall),
      call: inspectionSearchCall,
      onClose: () => setInspectionSearchCall(null),
    },
    aiCallIncomingPrompt: {
      open: Boolean(incomingCall) || incomingCallStatus === 'passed',
      status: incomingCallStatus,
      hasCall: Boolean(incomingCall),
      expiresAt: incomingCall ? incomingCall.expiresAt : null,
      submitting: incomingCallSubmitting,
      onAccept: acceptIncomingCall,
      onDecline: declineIncomingCall,
      onExpire: expireIncomingCall,
      onCloseStatus: closeIncomingCallStatus,
    },
  };
}

